/* custom-ecore-bottom-seo.js — mobile accordion for the "About [Brand]" content cards.
 *
 * WHY: the About cards are long-form prose (100-180 words each). Stacked vertically
 * on mobile they make the section extremely tall. This collapses each card to its
 * icon + title row; tapping expands the prose with a smooth height transition.
 *
 * SCOPE: mobile only (max-width: 768px). Desktop (>=769px) is never touched — the
 * 2-column grid renders every card fully expanded, exactly as before.
 *
 * NO-JS FALLBACK: every accordion CSS rule is gated on the `.is-accordion` class that
 * THIS script adds. With JS off, the class is never added, so all cards render fully
 * expanded — content is always in the DOM and visible (SEO-safe; user-initiated
 * disclosure only hides it visually once JS upgrades the section).
 *
 * Generalized: operates on every `.ecore-seo__cards` group on the page, so it serves
 * both custom-collections-bottom-seo (brand/child pages) and custom-ecore-bottom-seo.
 */
(function () {
  if (window.__ecoreSeoAccordionInit) return;
  window.__ecoreSeoAccordionInit = true;

  var MQ = window.matchMedia('(max-width: 768px)');
  var groups = [];
  var anchorTimer = null;

  function headOf(card) { return card.querySelector('.ecore-seo__card-head'); }
  function bodyOf(card) { return card.querySelector('.ecore-seo__card-body'); }
  function cardsOf(group) {
    return Array.prototype.slice.call(group.querySelectorAll(':scope > .ecore-seo__card'));
  }

  function openCard(card, instant) {
    var head = headOf(card), body = bodyOf(card);
    if (!head || !body) return;
    // Single-open: collapse siblings in the same group first.
    var group = card.parentNode;
    cardsOf(group).forEach(function (sib) { if (sib !== card && sib.classList.contains('is-open')) closeCard(sib); });
    card.classList.add('is-open');
    head.setAttribute('aria-expanded', 'true');
    if (instant) {
      body.style.maxHeight = 'none';
    } else {
      body.style.maxHeight = body.scrollHeight + 'px';
    }
  }

  function closeCard(card) {
    var head = headOf(card), body = bodyOf(card);
    if (!head || !body) return;
    // From current rendered height to 0 so the transition has two endpoints
    // (an element sitting at max-height:none can't animate down directly).
    body.style.maxHeight = body.scrollHeight + 'px';
    void body.offsetHeight; // force reflow to lock in the start height
    card.classList.remove('is-open');
    head.setAttribute('aria-expanded', 'false');
    body.style.maxHeight = '0px';
  }

  function toggleCard(card) {
    if (!MQ.matches) return; // never toggle on desktop
    // 50ms haptic on touch (no-op on desktop / unsupported).
    if (navigator.vibrate && window.matchMedia('(hover: none)').matches) navigator.vibrate(50);
    // Suppress the document's scroll-anchoring for the duration of the expand/collapse so the tapped
    // header stays put in frame (otherwise the browser anchors to content BELOW the growing card and
    // scrolls the header up out of view). Restored shortly after the transition.
    document.documentElement.style.overflowAnchor = 'none';
    clearTimeout(anchorTimer);
    anchorTimer = setTimeout(function () { document.documentElement.style.overflowAnchor = ''; }, 450);
    if (card.classList.contains('is-open')) closeCard(card);
    else openCard(card, false);
  }

  function onBodyTransitionEnd(e) {
    if (e.propertyName !== 'max-height') return;
    var body = e.currentTarget;
    var card = body.parentNode;
    // Once fully open, drop the fixed height so the body can reflow freely
    // (font loading, orientation change, links wrapping differently, etc.).
    if (card && card.classList.contains('is-open')) body.style.maxHeight = 'none';
  }

  function enable(group) {
    if (group.classList.contains('is-accordion')) return;
    group.classList.add('is-accordion');
    var idBase = group.__accGid || (group.__accGid = 'ecore-seo-acc-' + Math.random().toString(36).slice(2, 7));
    cardsOf(group).forEach(function (card, i) {
      var head = headOf(card), body = bodyOf(card);
      if (!head || !body) return;
      if (!body.id) body.id = idBase + '-' + i;
      head.setAttribute('role', 'button');
      head.setAttribute('tabindex', '0');
      head.setAttribute('aria-controls', body.id);
      head.setAttribute('aria-expanded', 'false');
      card.classList.remove('is-open');
      body.style.maxHeight = '0px';
      if (!head.__accWired) {
        head.__accWired = true;
        head.addEventListener('click', function () { toggleCard(card); });
        head.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') { e.preventDefault(); toggleCard(card); }
        });
        body.addEventListener('transitionend', onBodyTransitionEnd);
      }
    });
    // All cards start CLOSED on load (Ryan 2026-07-11 — no auto-expanded first card).
  }

  function disable(group) {
    if (!group.classList.contains('is-accordion')) return;
    group.classList.remove('is-accordion');
    cardsOf(group).forEach(function (card) {
      var head = headOf(card), body = bodyOf(card);
      if (body) body.style.maxHeight = '';
      if (head) {
        head.removeAttribute('role');
        head.removeAttribute('tabindex');
        head.removeAttribute('aria-controls');
        head.removeAttribute('aria-expanded');
      }
      card.classList.remove('is-open');
    });
  }

  function apply() {
    var mobile = MQ.matches;
    groups.forEach(function (g) { mobile ? enable(g) : disable(g); });
  }

  function init() {
    groups = Array.prototype.slice.call(document.querySelectorAll('.ecore-seo__cards'));
    if (!groups.length) return;
    apply();
    if (MQ.addEventListener) MQ.addEventListener('change', apply);
    else if (MQ.addListener) MQ.addListener(apply); // Safari < 14
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
